import React, { useRef, useContext, useState, useEffect } from 'react';
import { geoPath, geoEqualEarth, geoOrthographic, geoProjection } from 'd3-geo'
import world from '../data/mapData/world.topojson.json'
import countries from '../data/mapData/countries.json'
import sphere from '../data/mapData/sphere.json'
import regionsCountryList from '../data/regionsCountryList.json'
import chroma from 'chroma-js'
import styled from 'styled-components'
import { timer } from 'd3-timer'
import * as topojson from 'topojson'
import { select, mouse } from 'd3-selection'
import 'd3-transition'
import {ChartContext} from '../context/ChartContext'
import _ from 'lodash'
import {    blueRange, colorGreyLightest, ColorGreyLight, tealRange, purpleRange } from '../styles/sharedStyles'
import Tooltip from './Tooltip.jsx';

const Container = styled.div`
  width: 100%;
  height: 100%;
  transition: all 1s;

  display: flex;
  justify-content: center;
  align-items: center;

  canvas {
        transition: all 1s;
        width: ${props => props.dims}px;
        height: ${props => props.dims}px;
    }
`

const MapContainer = styled.div`
    display: inline-block;
    position: relative;
`

const SvgContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    z-index: ${props => props.zIndex || 0};
    pointer-events: ${props => props.pointerEvents || 'auto'};
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${props => props.cursor || "auto"};
`

let rotationOn
let tNew, dt, steps, pos, tOld, oldPos, countryHighlight, timerReset
tOld = 0;
oldPos = 0;
rotationOn = true
let rotationSpeed

const getColorScale = (data, range) => {
    const maxCountryTotal = _.maxBy(data, d => d.total)
    const minCountryTotal = _.minBy(data, d => d.total)
    const countryIds = _.uniq(data.map(d => d.cid))
    const colorScale = chroma.scale(range).domain([maxCountryTotal.total * .5, minCountryTotal.total]);
    return { colorScale, countryIds}
}

const getBoundingBox = ref => {
    const svg =  ref.current
    const rect = svg.getBoundingClientRect();
    return { svg, rect }
}

const getHoveredCountryInfo = (countryTotals, hoveredCountry) => {
    const hoveredCountryInfo = countryTotals.find(d => +d.cid === +hoveredCountry)
    return hoveredCountryInfo
}

                          
const WorldMap = () => {

    const canvasRef = useRef()
    const { countryTotals, position, reset, setReset } = useContext(ChartContext)
    const [ mousePosition , setMousePosition ] = useState('')
    const [ hoveredCountry , setHoveredCountry ] = useState('')

    position === 'right' ? rotationSpeed = 8000 : rotationSpeed = 25000
    reset ? timerReset = true : timerReset = false

    useEffect(() => 
       { 
        return () => {
            timerReset = true
            oldPos = 0
        }}
    ,[])
    
    const { colorScale, countryIds } = getColorScale(countryTotals, blueRange)

    const drawMap = pos => {

            const PIXEL_RATIO = window.devicePixelRatio * 2
            
            const canvas = canvasRef.current
            var rect = canvas.getBoundingClientRect();
            
            canvas.width = rect.width * PIXEL_RATIO;
            canvas.height = rect.height * PIXEL_RATIO;
            const context = canvas.getContext('2d')
            context.scale(PIXEL_RATIO, PIXEL_RATIO);
            
            const projection = geoOrthographic().rotate([pos, 0]).precision(0.1).fitSize([rect.width, rect.height], {type: "Sphere"})
            const path = geoPath(projection, context).pointRadius(1.5);
        
            context.clearRect(0, 0, rect.width, rect.height)
            
            context.fillStyle = "#fff"
            context.fillRect(0, 0, rect.width, rect.height)
    
            countries.features.filter(d => d.id !== "010").forEach(country => {
                context.beginPath()
                path({type: "FeatureCollection", features: [country]})
                const countryTotal = countryTotals.find(d => +d.cid === +country.id) 
                countryTotal 
                    ? context.fillStyle = +countryTotal.total === 0 
                                                    ? colorGreyLightest 
                                                    : colorScale(+countryTotal.total)
                    : context.fillStyle = colorGreyLightest
                context.fill();
            })
    
            context.beginPath()
            path(countries) 
            context.strokeStyle = '#fff'
            context.stroke();
    
            context.beginPath()
            path(sphere)
            context.strokeStyle = ColorGreyLight
            context.lineWidth = .5
            context.shadowColor = ColorGreyLight;
            context.shadowBlur = 5;
            context.shadowOffsetX = 4;
            context.shadowOffsetY = 4;
            context.stroke();
    
            context.canvas.onmousemove = e => {
                const pos = projection.invert([e.offsetX, e.offsetY])
                const hoveredOver = getCountry(pos)
                setMousePosition([e.offsetX, e.offsetY])
                if(hoveredOver && countryIds.includes(+hoveredOver.id)){
                    rotationOn = false
                    countryHighlight = getCountry(pos)
                    hoveredOver && hoveredCountry !== hoveredOver.id && setHoveredCountry(hoveredOver.id)
                } else {
                    rotationOn = true
                    countryHighlight = ''
                    setHoveredCountry(undefined)
                }
            }
    }

    const polygonContains = (polygon, point) => {
        var n = polygon.length
        var p = polygon[n - 1]
        var x = point[0], y = point[1]
        var x0 = p[0], y0 = p[1]
        var x1, y1
        var inside = false
        for (var i = 0; i < n; ++i) {
        p = polygon[i]
        x1 = p[0]
        y1 = p[1]
        if (((y1 > y) !== (y0 > y)) && (x < (x0 - x1) * (y - y1) / (y0 - y1) + x1)) inside = !inside
        x0 = x1
        y0 = y1
        }
        return inside
    }

    const getCountry = pos => {
        return countries.features.find(function(f) {
        return f.geometry.coordinates.find(function(c1) {
            return polygonContains(c1, pos) || c1.find(function(c2) {
            return polygonContains(c2, pos)
            })
        })
        })
    }

    const timerFunction  = () => {

        const rotationTimer = (now) => {
            if(rotationOn && !timerReset){
                tNew = now;
                dt = tOld - tNew;
                steps = dt * 360 / rotationSpeed;
                pos = rotationSpeed === 25000
                ? oldPos - steps
                : pos % 360 > 180
                ? oldPos - steps
                : oldPos + steps
                
                drawMap(pos)
                
                const direction = pos % 360 > 180 
                const limit = Math.round(pos/360) * 360
                const checker = direction ? limit <= Math.round(pos) : Math.round(pos) <= limit

                tOld = tNew;
                oldPos = rotationSpeed === 25000
                            ? pos
                            : checker
                                ? setReset(true)
                                : pos
            } else {
                tOld = now;
            }
        }

        const t = timer(now => {
            rotationTimer(now)
            if(timerReset) t.stop()
        })

    }

    (oldPos === 0 && !reset) && timerFunction()

    const hoveredCountryInfo = getHoveredCountryInfo(countryTotals, hoveredCountry)

    const resetTooltip = () => {
        if(hoveredCountry){
            rotationOn = true
            countryHighlight = ''
            setHoveredCountry(undefined)
        }
    }

    return (
        <>
            <Container
                onMouseOver={() => resetTooltip()}
                dims={position === 'left' ? 500 : 400}
            >
                <div/>
                {   !reset && 
                    <MapContainer>
                        <canvas
                            ref={canvasRef}
                            className={'canvas'}
                        />
                        <MapTooltip
                            mousePosition={mousePosition}
                            hoveredCountryInfo={hoveredCountryInfo} 
                            parentWidth={500}
                        />
                    </MapContainer>
                }
                <SvgMap
                    show={reset}
                />
            </Container>
        </>
    );
}

export default WorldMap;

const getCountryExist = (data, element) => {
    const countryExist = data.find(el => +el.cid === +element.id) 
    return countryExist
}

const getIsIncluded = (countryExist, filterValues, element, id = "id") => {
    const country = countryExist && element && regionsCountryList.find(el => +el.code === +element[id])
    const isRegionIncluded = country && filterValues.find(el => el === country.region)
    const isCountryIncluded = country && filterValues.find(el => el === country.country)
    return { country, isRegionIncluded, isCountryIncluded }
}

const SvgMap = ({show}) => {
    
    let updatePath
    const svgRef = useRef()
    const containerRef = useRef()
    const { countryTotals, path, windowWidth , setMapFilterValues,
            filteredHrdsData, filteredLesData, mapFilterValues,
            setFutureMapFilterValues, potentialCountryTotals } = useContext(ChartContext)
    const [ projectionChanged, setProjectionChanged ] = useState(false)
    const [ hoveredCountry, setHoveredCountry ] = useState('')
    const [ mousePosition, setMousePosition ] = useState([0,0])
    
    const { colorScale } = getColorScale(countryTotals, blueRange)
    const totalRespondents = _.sumBy(countryTotals, d => d.total)
    const fullData = path === "hrds" ? filteredHrdsData : filteredLesData
    useEffect(() => updatePath(), [totalRespondents, updatePath, mapFilterValues])

    const setClick = d => {
        const countryExist = getCountryExist(potentialCountryTotals.filter(el => el.total > 0), d) 
        if(countryExist){
            const { country, isRegionIncluded, isCountryIncluded } = getIsIncluded(countryExist, mapFilterValues, d)

            if(isRegionIncluded){
                const newFilterValues = mapFilterValues.filter(el => el !== country.region)
                setMapFilterValues([...newFilterValues, country.country])
                setFutureMapFilterValues([...newFilterValues, country.country])
            } else if (
                !isCountryIncluded
            ){
                setMapFilterValues([...mapFilterValues, country.country])
                setFutureMapFilterValues([...mapFilterValues, country.country])
            } else if (
                isCountryIncluded
            ){
                const newFilterValues = mapFilterValues.filter(el => el !== country.country)
                setMapFilterValues([...newFilterValues])
                setFutureMapFilterValues([...newFilterValues])
            }
        }
    }

    const setMove = d => {
        const mousePos = mouse(svgRef.current)
        setMousePosition(mousePos)
    }

    const setMouseEnter = d => {
        const countryExist = getCountryExist(fullData, d) 
        setHoveredCountry(d.id)
        if(countryExist){
            const { isRegionIncluded, isCountryIncluded, country } = getIsIncluded(countryExist, mapFilterValues, d)
            if(!isRegionIncluded && !isCountryIncluded){
                setFutureMapFilterValues([...mapFilterValues, country.country])
            }
        }
    }

    const setFill = (d, scale) => {
        const countryTotal = potentialCountryTotals.find(el => +el.cid === +d.id) 
        const countryExist = getCountryExist(fullData, d) 
        const { isRegionIncluded, isCountryIncluded } = getIsIncluded(countryExist, mapFilterValues, d)

        return countryTotal && +countryTotal.total > 0
                        ? (!!isRegionIncluded || !!isCountryIncluded)
                            ? scale(+countryTotal.total) 
                            : ColorGreyLight
                        : colorGreyLightest
    }

    const svgMap = () => {
        const { svg } = getBoundingBox(svgRef)

        select(svg)
            .attr('height', '40rem')
            .attr('width', '100%')

        const { rect } = getBoundingBox(svgRef)
        const projection = geoOrthographic().rotate([0, 0]).precision(0.1).fitSize([rect.width, rect.height], {type: "Sphere"})
        const path = geoPath().projection(projection);
        const geojson = topojson.feature(world, world.objects.countries)

        select(svg)
          .selectAll('path')
          .data(geojson.features)
          .enter()
          .append("path")
          .attr("d", path)
          .style("fill", d => {
            const countryTotal = countryTotals.find(el => +el.cid === +d.id) 
            return countryTotal ? colorScale(+countryTotal.total) : colorGreyLightest
          })
          .style("stroke", "#fff")
          .style("stroke-width", ".5px")
        //   .attr('cursor', d => setCursor(d))
          .on('mousemove', d => setMove(d))
          .on("mouseout", () => {setMousePosition([0,0])})
          .on("mouseover", d => setMouseEnter(d))
    }
    
    const projectionTween = (projection0, projection1) => {
    return function(d) {
        var t = 0;
        const rect = svgRef.current.getBoundingClientRect();

        const project = (λ, φ) => {
        λ *= 180 / Math.PI
        φ *= 180 / Math.PI
        const p0 = projection0([λ, φ])
        const p1 = projection1([λ, φ]);
        return [(1 - t) * p0[0] + t * p1[0], (1 - t) * -p0[1] + t * -p1[1]];
        }

        var projection = geoProjection(project)
            .scale(1)
            .translate([rect.width / 2, rect.height / 2]);
        var path = geoPath(projection);

        return function(_) {
        t = _;
        return path(d);
        };
    };
    }
    
    const updateProjection = () => {
        const { svg } = getBoundingBox(svgRef)
        select(svg)
            .attr('height', '40rem')
            .attr('width', '100%')
        const { rect } = getBoundingBox(svgRef)
        const projectionOne = geoOrthographic().rotate([0, 0]).precision(0.1).fitSize([rect.width, rect.height], {type: "Sphere"})
        const projectionTwo = geoEqualEarth().rotate([0, 0]).precision(0.1).fitSize([rect.width , rect.height ], {type: "Sphere"}).scale(145)
        const colorRange = path === 'hrds' ? tealRange : purpleRange
        
        const { colorScale } = getColorScale(countryTotals, colorRange)
        select(svg)
            .selectAll("path")
            .transition()
            .duration(2000)
            .attrTween("d", projectionTween(projectionOne, projectionTwo))
            // .attr('cursor', d => setCursor(d))
            .style("fill", d => setFill(d, colorScale))

        select(svg)
            .selectAll("path")
            .on("click", d => setClick(d))
            .on('mousemove', d => setMove(d))
            .on("mouseover", d => setMouseEnter(d))

        setProjectionChanged(true)
        setTimeout(() => updateDims(1000), 2000)
        
    }

    updatePath = () => {
        const { svg } = getBoundingBox(svgRef)
        const colorRange = path === 'hrds' ? tealRange : purpleRange
        const { colorScale } = getColorScale(countryTotals, colorRange)
        select(svg)
            .selectAll("path")
            .transition()
            .duration(1000)
            //.attr('cursor', d => setCursor(d))
            .style("fill", d => setFill(d, colorScale))
        
        select(svg)
            .selectAll("path")
            .on("click", d => setClick(d))
            .on('mousemove', d => setMove(d))
            .on("mouseover", d => setMouseEnter(d))

    }

    const updateDims = (duration) => {
        const { svg } = getBoundingBox(svgRef)

        const { rect } = getBoundingBox(svgRef)
        const projection = geoEqualEarth().rotate([0, 0]).precision(0.1).fitSize([rect.width , rect.height ], {type: "Sphere"}).scale(145)
        const path = geoPath().projection(projection);

        select(svg)
          .selectAll('path')
          .transition('test')
          .duration(duration)
          .attr("d", path)
    }

    useEffect(() => {
        svgMap()
    }, [])

    useEffect(() => {
        projectionChanged &&
        updatePath()
    }, [path])

    useEffect(() => {
        updateDims(0)
    }, [windowWidth])

    const hoveredCountryInfo = getHoveredCountryInfo(countryTotals, hoveredCountry)
    const hoveredFutureCountryInfo = getHoveredCountryInfo(potentialCountryTotals, hoveredCountry)

    show && !projectionChanged && updateProjection()
    const svgContainer = containerRef.current && containerRef.current.getBoundingClientRect()
    return  (
                <SvgContainer
                    zIndex={show ? 9 : -9}
                    ref={containerRef}
                    cursor={hoveredFutureCountryInfo && hoveredFutureCountryInfo.total > 0 && "pointer"}
                    data-tut="svg-map"
                >
                    <svg
                        ref={svgRef}    
                    />
                    {
                        hoveredFutureCountryInfo 
                        && hoveredFutureCountryInfo.total >  0 
                        &&
                        <MapTooltip
                            hoveredCountryInfo={hoveredCountryInfo}
                            hoveredFutureCountryInfo={hoveredFutureCountryInfo}
                            mousePosition={mousePosition}
                            parentWidth={svgContainer && svgContainer.width}
                            fullData={fullData}
                        />
                    }
                </SvgContainer>)
}

const MapTooltip = ({ mousePosition, hoveredCountryInfo, hoveredFutureCountryInfo, parentWidth, fullData }) => {

    const { path } = useContext(ChartContext)
    const tooltipRef = useRef()
    const tooltipDims = tooltipRef.current && tooltipRef.current.getBoundingClientRect()
    const orientation = mousePosition[0] < parentWidth/2 ? 'left' : 'right'
    const leftPosition = orientation === 'left'
                    ? mousePosition[0] - (tooltipDims && tooltipDims.width) - 15
                    : mousePosition[0] + 15
    const hoveredId = hoveredCountryInfo ? hoveredCountryInfo.cid : 0
    const countryExist = fullData ? fullData.find(el => +el.cid === +hoveredId) : true
    
    return (
        <>
            { hoveredCountryInfo && (mousePosition[1] > 1 && mousePosition[0] > 1)  &&
                <Tooltip
                        ref={tooltipRef}
                        top={mousePosition[1] - ((tooltipDims && tooltipDims.height/2)) }
                        left={leftPosition}
                        visible={countryExist && tooltipDims}
                        position={orientation}
                >
                    <div className="title">
                        {hoveredCountryInfo.countryName}
                    </div>
                    <div className="value">
                        {
                            hoveredCountryInfo.total !== 0 ?
                            <>
                                <div className="value__text">
                                    No. of {
                                        path === "both" 
                                            ? "HRDs and LEs "
                                            : path === "hrds"
                                                ? "HRDs "
                                                : "LEs " 
                                    }
                                    surveyed: &nbsp;
                                </div>
                                <div className="value__value">
                                    {' '}{hoveredCountryInfo.total}
                                </div>
                            </> : 
                            <>
                                <div className="value__text">
                                    Click to add to selection! 
                                        (+{hoveredFutureCountryInfo.total} 
                                        {hoveredFutureCountryInfo.total > 1 
                                            ? " respondents" 
                                            : " respondent"})
                                </div>
                            </>
                        }
                    </div>
                </Tooltip>
            }
        </>
    )
}